import type {
  OperationResponse,
  Parameters,
  UnknownParamsObject,
} from "../../common";
import type * as Paths from "./paths";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { omitPathParameters, replacePathParameters } from "../../utils";

interface OperationMethods {
  "getList"(
    parameters?: Parameters<Paths.GetList.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetList.Responses.$200>;

  "getNewForm"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetNewForm.Responses.$200>;

  "createRecord"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateRecord.Responses.$200>;

  "getRecord"(
    parameters?: Parameters<
      Paths.GetRecord.QueryParameters & Paths.GetRecord.PathParameters
    > | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetRecord.Responses.$200>;

  "updateRecord"(
    parameters?: Parameters<Paths.UpdateRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateRecord.Responses.$200>;

  "deleteRecord"(
    parameters?: Parameters<Paths.DeleteRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteRecord.Responses.$204>;
}

export const getAccountingTemplateOperations = (client: AxiosInstance) => {
  const getList: OperationMethods["getList"] = (parameters, data, config) => {
    const url = "/accounting-template";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const getNewForm: OperationMethods["getNewForm"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/accounting-template/new-form";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createRecord: OperationMethods["createRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/accounting-template";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getRecord: OperationMethods["getRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/accounting-template/{id}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateRecord: OperationMethods["updateRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/accounting-template/{id}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteRecord: OperationMethods["deleteRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/accounting-template/{id}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  return {
    getList,
    createRecord,
    getRecord,
    updateRecord,
    deleteRecord,
    getNewForm,
  };
};
